import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { QrCode, Smartphone, ArrowRight, MapPin, Share2, Navigation, Luggage, Store, Users, Trophy, Zap, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export const CTA = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [selectedMode, setSelectedMode] = useState('travel'); // 'travel' or 'guestbook'
  const [showPointsGuide, setShowPointsGuide] = useState(false);
  
  const travelSteps = [
    {
      icon: QrCode,
      title: "Create & Attach QR Code",
      description: "Generate a unique QR code and attach it to your item (backpack, notebook, gift, etc.).",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: Share2,
      title: "Send It On Its Journey",
      description: "Give it away, sell it, or pass it along. As it travels, finders scan and add their stories.",
      color: "from-blue-500 to-purple-500"
    },
    {
      icon: MapPin,
      title: "Track Across the World",
      description: "Watch your item travel from city to city, collecting stories and connections along the way.",
      color: "from-purple-500 to-pink-500"
    }
  ];

  const guestbookSteps = [
    {
      icon: Store,
      title: "Create a Location",
      description: "Set up a digital guestbook for your business, event, landmark, or favorite spot.",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: QrCode,
      title: "Display QR Code or Use GPS",
      description: "Print QR codes to display, or visitors can sign when they're near your GPS coordinates.",
      color: "from-blue-500 to-purple-500"
    },
    {
      icon: Users,
      title: "Collect Visitor Stories",
      description: "Visitors sign your guestbook by scanning or being nearby, sharing their experience and photos.",
      color: "from-purple-500 to-pink-500"
    }
  ];

  const steps = selectedMode === 'travel' ? travelSteps : guestbookSteps;

  return (
    <section 
      className="py-16 text-white relative overflow-hidden"
      style={{
        background: theme.id === 'vintage' 
          ? 'linear-gradient(135deg, #8B4513 0%, #654321 50%, #3d2817 100%)'
          : 'linear-gradient(to bottom right, rgb(16 185 129), rgb(5 150 105), rgb(37 99 235))',
        fontFamily: theme.id === 'vintage' ? 'Georgia, serif' : 'inherit'
      }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-blue-400/10 rounded-full blur-xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          
          {/* How It Works Section */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto leading-relaxed mb-8">
              Two ways to connect: Track traveling items or create location guestbooks
            </p>

            {/* Mode Selector */}
            <div className="flex justify-center gap-3 mb-8">
              <button
                onClick={() => setSelectedMode('travel')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
                  selectedMode === 'travel'
                    ? 'bg-white text-emerald-600 shadow-xl scale-105'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                <Luggage className="w-5 h-5" />
                Traveling Items
              </button>
              <button
                onClick={() => setSelectedMode('guestbook')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
                  selectedMode === 'guestbook'
                    ? 'bg-white text-blue-600 shadow-xl scale-105'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                <Navigation className="w-5 h-5" />
                Location Guestbooks
              </button>
            </div>

            {/* Description based on selected mode */}
            <div className="mb-10 max-w-3xl mx-auto">
              {selectedMode === 'travel' ? (
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Track Items as They Travel
                  </h3>
                  <p className="text-emerald-100">
                    Attach a QR code to backpacks, notebooks, gifts, or products. As your item travels from person to person and place to place, each finder can scan the code and add their story, creating a digital journey map.
                  </p>
                </div>
              ) : (
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Create Digital Guestbooks
                  </h3>
                  <p className="text-blue-100">
                    Perfect for businesses, events, or special places. Visitors can sign your digital guestbook by scanning a QR code or simply being near your location (GPS). Everyone who visits can leave their story and photo.
                  </p>
                </div>
              )}
            </div>

            {/* Steps Grid */}
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
              {steps.map((step, index) => (
                <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 bg-white/95 backdrop-blur-sm">
                  <CardContent className="p-6 text-center relative overflow-hidden">
                    {/* Step Number */}
                    <div className="absolute top-3 right-3 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-sm font-bold text-emerald-600">
                      {index + 1}
                    </div>

                    {/* Gradient Icon Background */}
                    <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-xl`}>
                      <step.icon className="w-8 h-8 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Connecting arrow (except for last item) */}
                    {index < steps.length - 1 && (
                      <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-emerald-300 to-white/50"></div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Additional Info */}
            <p className="text-sm text-emerald-100 max-w-2xl mx-auto opacity-90 mb-8">
              {selectedMode === 'travel' 
                ? 'Your item becomes a traveling storyteller, creating connections across cities and countries.'
                : 'Build a collection of memories from everyone who visits your special place.'}
            </p>

            {/* Points System Guide - Collapsible */}
            <div className="max-w-3xl mx-auto mb-8">
              <button
                onClick={() => setShowPointsGuide(!showPointsGuide)}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-all duration-300"
              >
                <Trophy className="w-5 h-5 text-amber-300" />
                <span className="font-semibold text-white">Points & Leaderboard System</span>
                {showPointsGuide ? (
                  <ChevronUp className="w-5 h-5 text-white" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white" />
                )}
              </button>
              
              {showPointsGuide && (
                <div className="mt-4 bg-white rounded-xl shadow-2xl overflow-hidden text-left">
                  <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 py-4">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Star className="w-5 h-5" />
                      Earn Points & Climb the Leaderboard
                    </h3>
                  </div>
                  
                  <div className="p-5">
                    {/* Active Points */}
                    <div className="mb-5">
                      <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                        <Zap className="w-4 h-4 text-blue-500" />
                        Active Points (Your Actions)
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                          <span className="text-lg font-bold text-blue-600">+5</span>
                          <span className="text-xs text-blue-800">Add story to item</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                          <span className="text-lg font-bold text-emerald-600">+25</span>
                          <span className="text-xs text-emerald-800">QR scan guestbook</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
                          <span className="text-lg font-bold text-purple-600">+10</span>
                          <span className="text-xs text-purple-800">GPS check-in</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                          <span className="text-lg font-bold text-amber-600">+100</span>
                          <span className="text-xs text-amber-800">Founder bonus</span>
                        </div>
                      </div>
                    </div>

                    {/* Passive Points */}
                    <div className="mb-4">
                      <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        Passive Points (Keep Earning!)
                      </h4>
                      <p className="text-xs text-gray-600 mb-2">
                        Earn even after you've moved on when your items continue traveling.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg border border-pink-200">
                          <span className="text-lg font-bold text-pink-600">+3</span>
                          <span className="text-xs text-pink-800">Someone scans your item</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
                          <span className="text-lg font-bold text-violet-600">+8</span>
                          <span className="text-xs text-violet-800">New story on your item</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>📊 Leaderboard:</strong> Top contributors featured monthly. Points apply to all items where you've added a story with your email.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Ready to Start Section */}
          <div className="text-center">
            <div className="mb-8">
              <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Ready to Start Your
                <span className="block">Item's Adventure?</span>
              </h2>
              <p className="text-xl md:text-2xl text-emerald-100 mb-8 max-w-3xl mx-auto leading-relaxed">
                Join thousands of people creating magical journeys for everyday items. 
                It takes less than 2 minutes to get started.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <Button 
                size="lg" 
                className={theme.id === 'vintage' 
                  ? "bg-[#f4e4c1] text-[#654321] hover:bg-[#f5ebd2] px-8 py-4 text-lg rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 font-bold border-2 border-[#8B4513]"
                  : "bg-white text-emerald-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 font-semibold"
                }
                style={{ fontFamily: theme.id === 'vintage' ? 'Georgia, serif' : 'inherit' }}
                onClick={() => navigate('/create-item')}
              >
                <QrCode className="w-6 h-6 mr-3" />
                Create a QR code to attach to an item
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className={theme.id === 'vintage'
                  ? "border-3 border-[#f4e4c1] text-[#f4e4c1] hover:bg-[#f4e4c1] hover:text-[#654321] px-8 py-4 text-lg rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 font-bold"
                  : "border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-4 text-lg rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 font-semibold"
                }
                style={{ fontFamily: theme.id === 'vintage' ? 'Georgia, serif' : 'inherit' }}
                onClick={() => navigate('/qr-scanner')}
              >
                <Smartphone className="w-6 h-6 mr-3" />
                Scan an Existing Item
              </Button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                {
                  title: "Free to Start",
                  description: "Create your first 5 QR codes completely free. No credit card required."
                },
                {
                  title: "Global Community",
                  description: "Connect with travelers and storytellers from around the world."
                },
                {
                  title: "Email Updates",
                  description: "Get notified whenever someone adds to your item's story."
                }
              ].map((feature, index) => (
                <div key={index} className="text-center">
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-emerald-100 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="mt-16 pt-8 border-t border-white/20">
              <p className="text-emerald-100 mb-4">Trusted by adventurers worldwide</p>
              <div className="flex justify-center items-center space-x-8 text-white/80">
                <span className="text-sm">✓ SSL Secured</span>
                <span className="text-sm">✓ Privacy Protected</span>
                <span className="text-sm">✓ 24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute bottom-10 left-10 animate-pulse">
        <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3">
          <QrCode className="w-8 h-8 text-white/80" />
        </div>
      </div>
      <div className="absolute top-20 right-10 animate-bounce delay-1000">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3">
          <Smartphone className="w-8 h-8 text-white/80" />
        </div>
      </div>
    </section>
  );
};